/**
 * Pure encoders/decoders for the Shooters Global SG Timer BLE API v3.2.
 * Lives in @flm9/scoring so the framing logic can be unit-tested without
 * a Web Bluetooth runtime — the consumer (apps/referee) glues this to
 * `navigator.bluetooth` notifications.
 *
 * All multibyte values are big-endian per the spec.
 *
 * Spec reference (downloaded 2026-05-07):
 *   https://timer.shooters.global/sg_timer_public_bt_api
 */

export const SG_TIMER_SERVICE_UUID = '7520ffff-14d2-4cda-8b6b-697c554c9311';
export const SG_TIMER_NAME_PREFIX = 'SG-SST4';

export const SG_CHAR = {
  command: '75200000-14d2-4cda-8b6b-697c554c9311',
  event: '75200001-14d2-4cda-8b6b-697c554c9311',
  savedSessionIdList: '75200002-14d2-4cda-8b6b-697c554c9311',
  shotList: '75200004-14d2-4cda-8b6b-697c554c9311',
  parSetup: '75200005-14d2-4cda-8b6b-697c554c9311',
  unixTime: '75200006-14d2-4cda-8b6b-697c554c9311',
  apiVersion: '7520fffe-14d2-4cda-8b6b-697c554c9311',
} as const;

export const SgCommandId = {
  SESSION_START: 0x00,
  SESSION_SUSPEND: 0x01,
  SESSION_RESUME: 0x02,
  SESSION_STOP: 0x03,
} as const;
export type SgCommandId = (typeof SgCommandId)[keyof typeof SgCommandId];

export const SgEventId = {
  SESSION_STARTED: 0x00,
  SESSION_SUSPENDED: 0x01,
  SESSION_RESUMED: 0x02,
  SESSION_STOPPED: 0x03,
  SHOT_DETECTED: 0x04,
  SESSION_SET_BEGIN: 0x05,
} as const;
export type SgEventId = (typeof SgEventId)[keyof typeof SgEventId];

/**
 * Web Bluetooth's `writeValueWithResponse` wants a concrete
 * `ArrayBuffer`-backed view; TS 5.7+ types `new Uint8Array(n)` as
 * `Uint8Array<ArrayBufferLike>` which is incompatible. Always allocate
 * via an explicit `ArrayBuffer` so the return type is `Uint8Array<ArrayBuffer>`.
 */
export type SgBytes = Uint8Array<ArrayBuffer>;

function alloc(n: number): SgBytes {
  return new Uint8Array(new ArrayBuffer(n)) as SgBytes;
}

/**
 * Input type for the decoders. Web Bluetooth surfaces
 * `DataView<ArrayBufferLike>` which TS 5.7+'s tighter `BufferSource`
 * (≡ ArrayBuffer | ArrayBufferView<ArrayBuffer>) refuses, so we accept
 * the wider union and normalise inside.
 */
export type SgInput = DataView | ArrayBufferView | ArrayBuffer | SharedArrayBuffer;

function asDataView(buf: SgInput): DataView {
  if (buf instanceof DataView) return buf;
  if (ArrayBuffer.isView(buf)) {
    return new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  }
  return new DataView(buf as ArrayBuffer);
}

/**
 * The COMMAND characteristic accepts `[len, cmd_id, …data]` and the
 * argument-less commands are all `[0x01, cmd_id]` (len = bytes after len).
 */
export function encodeCommand(cmdId: SgCommandId, data?: SgInput): SgBytes {
  const dataView = data ? asDataView(data) : null;
  const dataLen = dataView?.byteLength ?? 0;
  const out = alloc(2 + dataLen);
  out[0] = 1 + dataLen;
  out[1] = cmdId;
  if (dataView) {
    for (let i = 0; i < dataLen; i++) out[2 + i] = dataView.getUint8(i);
  }
  return out;
}

/**
 * PAR_SETUP read/write payload. start_delay/time_limit are in units of
 * 0.1 s. start_delay = 0xFFFF means random 1.0-4.0 s. shot_limit = 0
 * means unlimited.
 */
export function encodeParSetup(
  startDelayDS: number,
  timeLimitDS: number,
  shotLimit: number,
): SgBytes {
  const out = alloc(6);
  const dv = new DataView(out.buffer);
  dv.setUint16(0, startDelayDS, false);
  dv.setUint16(2, timeLimitDS, false);
  dv.setUint16(4, shotLimit, false);
  return out;
}

export function decodeParSetup(buf: SgInput): {
  startDelayDS: number;
  timeLimitDS: number;
  shotLimit: number;
} {
  const dv = asDataView(buf);
  return {
    startDelayDS: dv.getUint16(0, false),
    timeLimitDS: dv.getUint16(2, false),
    shotLimit: dv.getUint16(4, false),
  };
}

export type SgEvent =
  | { kind: 'session_started'; sessId: number; startDelayDS: number }
  | { kind: 'session_suspended'; sessId: number; totalShots: number }
  | { kind: 'session_resumed'; sessId: number; totalShots: number }
  | { kind: 'session_stopped'; sessId: number; totalShots: number }
  | { kind: 'shot_detected'; sessId: number; shotNum: number; shotTimeMs: number }
  | { kind: 'session_set_begin'; sessId: number };

/**
 * Decode one EVENT-characteristic notification frame. Returns null when
 * the payload is too short to identify or when the event id is not
 * recognised — caller should keep the raw bytes for the debug screen.
 */
export function decodeEvent(buf: SgInput): SgEvent | null {
  const dv = asDataView(buf);
  if (dv.byteLength < 2) return null;
  const len = dv.getUint8(0);
  if (dv.byteLength < 1 + len) return null;
  const eventId = dv.getUint8(1);
  switch (eventId) {
    case SgEventId.SESSION_STARTED:
      if (dv.byteLength < 8) return null;
      return {
        kind: 'session_started',
        sessId: dv.getUint32(2, false),
        startDelayDS: dv.getUint16(6, false),
      };
    case SgEventId.SESSION_SUSPENDED:
      if (dv.byteLength < 8) return null;
      return {
        kind: 'session_suspended',
        sessId: dv.getUint32(2, false),
        totalShots: dv.getUint16(6, false),
      };
    case SgEventId.SESSION_RESUMED:
      if (dv.byteLength < 8) return null;
      return {
        kind: 'session_resumed',
        sessId: dv.getUint32(2, false),
        totalShots: dv.getUint16(6, false),
      };
    case SgEventId.SESSION_STOPPED:
      if (dv.byteLength < 8) return null;
      return {
        kind: 'session_stopped',
        sessId: dv.getUint32(2, false),
        totalShots: dv.getUint16(6, false),
      };
    case SgEventId.SHOT_DETECTED:
      if (dv.byteLength < 12) return null;
      return {
        kind: 'shot_detected',
        sessId: dv.getUint32(2, false),
        shotNum: dv.getUint16(6, false),
        shotTimeMs: dv.getUint32(8, false),
      };
    case SgEventId.SESSION_SET_BEGIN:
      if (dv.byteLength < 6) return null;
      return { kind: 'session_set_begin', sessId: dv.getUint32(2, false) };
    default:
      return null;
  }
}

/**
 * Decode a COMMAND characteristic response notification:
 * `[len=2, cmd_id, resp_code]`.
 */
export function decodeCommandResponse(buf: SgInput): {
  cmdId: number;
  ok: boolean;
} | null {
  const dv = asDataView(buf);
  if (dv.byteLength < 3) return null;
  return { cmdId: dv.getUint8(1), ok: dv.getUint8(2) === 0x00 };
}

export function encodeUnixTime(unixSeconds: number): SgBytes {
  const out = alloc(4);
  new DataView(out.buffer).setUint32(0, unixSeconds >>> 0, false);
  return out;
}

export function decodeApiVersion(buf: SgInput): string {
  const dv = asDataView(buf);
  let out = '';
  for (let i = 0; i < dv.byteLength; i++) out += String.fromCharCode(dv.getUint8(i));
  return out;
}

export function bytesToHex(buf: SgInput): string {
  const dv = asDataView(buf);
  const parts: string[] = [];
  for (let i = 0; i < dv.byteLength; i++) {
    parts.push(dv.getUint8(i).toString(16).padStart(2, '0'));
  }
  return parts.join(' ');
}
